import React, { useState } from "react";
import { useSelector } from 'react-redux';
import { useTranslation } from "react-i18next";

import './css/index.scss';

import {
  Input,
  Form,
  Button,
} from '@lockerpm/design';

import authComponents from "./components";
import itemsComponents from "../../components/items";

import userServices from "../../services/user";
import authServices from "../../services/auth";
import commonServices from "../../services/common";

import global from "../../config/global";

const CreateMasterPassword = () => {
  const { AuthCard, CreateMPConfirmModal } = authComponents;
  const { UserInfo } = itemsComponents;
  const { t } = useTranslation();

  const [callingAPI, setCallingAPI] = useState(false);
  const [confirmVisible, setConfirmVisible] = useState(false);

  const [form] = Form.useForm();

  const locale = useSelector((state) => state.system.locale);
  const userInfo = useSelector((state) => state.auth.userInfo);

  const masterPassword = Form.useWatch('masterPassword', form) || '';
  const masterPasswordHint = Form.useWatch('masterPasswordHint', form) || '';

  const handleCreateMasterPassword = async () => {
    form.validateFields().then(() => {
      setConfirmVisible(true)
    })
  }

  const handleUnlock = async (payload) => {
    await userServices.new_session(payload).then(async (response) => {
      await commonServices.update_auth_info({
        ...response,
        password: masterPassword,
        username: userInfo?.email
      });
      await commonServices.sync_profile();
      await commonServices.sync_data();
    }).catch((error) => {
      global.pushError(error)
    });
  }

  const createMasterPassword = async () => {
    setCallingAPI(true);
    const payload = {
      email: userInfo?.email,
      password: masterPassword,
      password_hint: masterPasswordHint,
      kdf: global.constants.CORE_JS_INFO.KDF,
      kdf_iterations: global.constants.CORE_JS_INFO.KDF_ITERATIONS,
      kdf_memory: null,
      kdf_parallelism: null
    }
    await userServices.register(payload).then(async () => {
      await handleUnlock(payload);
    }).catch((error) => {
      global.pushError(error)
    })

    setCallingAPI(false)
  }

  return (
    <div
      className="auth-page"
    >
      <AuthCard
        className="w-[600px]"
        other={
          <div className="mt-4 text-center">
            <span>
              {t('setup_2fa.note')}
              <Button
                type="link"
                className="font-semibold"
                onClick={() => authServices.logout(true)}
              >
                {t('button.logout')}
              </Button>
            </span>
          </div>
        }
      >
        <div className="w-full">
          <div className="w-full flex items-center justify-between">
            <p className="text-2xl font-semibold">
              {t('create_master_password.title')}
            </p>
          </div>
          <p className="mb-6 mt-2">{t('create_master_password.description')}</p>
          <Form
            form={form}
            key={locale}
          >
            <div className="mt-6 mb-10 flex items-center justify-center">
              <UserInfo />
            </div>
            <Form.Item
              name="masterPassword"
              rules={[
                global.rules.REQUIRED(t('lock.master_password')),
                global.rules.LATEST_LENGTH(t('lock.master_password'), 8),
              ]}
            >
              <Input.Password
                placeholder={t('lock.master_password')}
                size="large"
                disabled={callingAPI}
                onPressEnter={handleCreateMasterPassword}
              />
            </Form.Item>
            <Form.Item
              name="confirmMasterPassword"
              rules={[
                global.rules.REQUIRED(t('create_master_password.confirm_master_password')),
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (value && getFieldValue('masterPassword') !== value) {
                      return Promise.reject(new Error(t('validation.passwords_not_match')));
                    }
                    return Promise.resolve()
                  },
                }),
              ]}
            >
              <Input.Password
                placeholder={t('create_master_password.confirm_master_password')}
                size="large"
                disabled={callingAPI}
                onPressEnter={handleCreateMasterPassword}
              />
            </Form.Item>
            <Form.Item
              name="masterPasswordHint"
            >
              <Input.Password
                placeholder={t('create_master_password.master_password_hint')}
                size="large"
                disabled={callingAPI}
                onPressEnter={handleCreateMasterPassword}
              />
            </Form.Item>
          </Form>
          <Button
            className="w-full"
            size="large"
            type="primary"
            htmlType="submit"
            loading={callingAPI}
            onClick={handleCreateMasterPassword}
          >
            {t('create_master_password.title')}
          </Button>
          <p className="mb-6 mt-2">{t('create_master_password.note')}</p>
        </div>
      </AuthCard>
      <CreateMPConfirmModal
        visible={confirmVisible}
        callingAPI={callingAPI}
        onClose={() => setConfirmVisible(false)}
        onConfirm={() => createMasterPassword()}
      />
    </div>
  );
}

export default CreateMasterPassword;